"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "energy-cost-calculator",
  "refrigerator-size-calculator",
  "appliance-energy-calculator",
  "refrigerator-lifespan-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Coil Cleaning Savings Calculator",
  category: "Energy & Cost" as const,
  mediumDescription:
    "Estimate the kWh and dollars you're losing to dirty condenser coils, and the payback period for cleaning them yourself or hiring it out. Uses DOE-published efficiency-loss ranges and the EIA US average residential electricity rate.",
  faqs: [
    { question: "How much energy do dirty coils actually waste?", answer: "The US Department of Energy estimates dirty condenser coils can increase a refrigerator's energy use by 25% or more by forcing the compressor to run longer to reject heat. The exact amount depends on dust load and how long since last cleaning." },
    { question: "How often should I clean my fridge coils?", answer: "ENERGY STAR recommends cleaning coils at least every 6–12 months. Homes with pets, smokers, or dusty environments should clean every 3–6 months. Built-in models with restricted airflow benefit from more frequent cleaning." },
    { question: "Is DIY coil cleaning worth it vs hiring a pro?", answer: "DIY costs about $15 (coil brush + vacuum attachment) and takes 30–45 minutes. Professional appliance service typically runs $100–$150 but includes a broader inspection. For a healthy fridge, DIY is usually the better ROI." },
    { question: "Where are the condenser coils located?", answer: "On most modern refrigerators, coils are at the bottom front (behind the kick-plate grille) or on the back. Bottom-mounted coils accumulate more dust because they're exposed to floor-level airflow." },
    { question: "Can dirty coils damage the compressor?", answer: "Yes. When coils can't reject heat, the compressor runs hotter and longer, which shortens its operating life. Most compressor failures are downstream of poor airflow or dirty coils." },
    { question: "What does the EIA US average electricity rate of 13¢/kWh mean for my savings?", answer: "13¢/kWh is the latest EIA published US average residential rate. Multiply your wasted kWh by your local rate (often higher in CA, NY, HI; lower in WA, ID, LA) to get a state-accurate dollar figure." },
  ],
  relatedTools: ["energy-cost-calculator", "refrigerator-size-calculator", "appliance-energy-calculator"],
}

export function CondenserCoilCleaningRoiPageClient() {
  const [currentKwh, setCurrentKwh] = useState("500")
  const [lastCleaned, setLastCleaned] = useState("never")
  const [cleaningMethod, setCleaningMethod] = useState("diy")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // EnergyGuide range for a household refrigerator: never under 50 kWh/yr, never over 5,000.
    const kwh = readQuantity(currentKwh, { min: 50, max: 5000 })
    if (kwh === null) return null

    let efficiencyLoss = 0
    if (lastCleaned === "never") efficiencyLoss = 0.25
    else if (lastCleaned === "2+years") efficiencyLoss = 0.20
    else if (lastCleaned === "1year") efficiencyLoss = 0.10
    else efficiencyLoss = 0.05

    const electricityRate = 0.13
    const currentAnnualCost = kwh * electricityRate
    const cleanedKwh = kwh / (1 + efficiencyLoss)
    const cleanedAnnualCost = cleanedKwh * electricityRate
    const annualSavings = currentAnnualCost - cleanedAnnualCost

    const cleaningCost = cleaningMethod === "diy" ? 15 : 100
    const roi = (annualSavings / cleaningCost) * 100
    const paybackMonths = (cleaningCost / annualSavings) * 12
    const fiveYearSavings = annualSavings * 5 - cleaningCost * 2.5

    let recommendation = ""
    if (efficiencyLoss >= 0.20) recommendation = "Urgent — clean immediately to stop significant energy waste."
    else if (efficiencyLoss >= 0.10) recommendation = "Recommended — clean soon to restore efficiency."
    else if (efficiencyLoss >= 0.05) recommendation = "Schedule a cleaning soon to keep losses minor."
    else recommendation = "On track — keep up the regular maintenance schedule."

    return {
      currentAnnualCost,
      cleanedAnnualCost,
      annualSavings,
      cleaningCost,
      roi,
      paybackMonths,
      fiveYearSavings,
      efficiencyLoss: efficiencyLoss * 100,
      recommendation,
      kwhWasted: kwh - cleanedKwh,
    }
  }, [currentKwh, lastCleaned, cleaningMethod])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What are dirty coils"
      heroTitleItalic="actually"
      heroTitlePost="costing you?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "DOE efficiency-loss + EIA rate"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "+25%", label: "Energy use", note: "Never-cleaned (DOE)" },
            { value: "$0.13", label: "kWh", note: "EIA US avg" },
            { value: "$15", label: "DIY cost", note: "Brush + vacuum" },
            { value: "6–12 mo", label: "Cleaning interval", note: "ENERGY STAR" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-coil-cleaning-savings-calculator")}
      cta={{
        title: "Old fridge bleeding energy? Upgrade to French door.",
        body: "Browse French door refrigerators at Fridge.com — modern inverter compressors and accessible front-mounted coils that are easier to keep clean.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Three inputs.
                <br />
                Real payback.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                We apply DOE efficiency-loss ranges to your current kWh, then divide cleaning cost by annual savings.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Current annual kWh
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={50}
                      max={5000}
                      step={1}
                      value={currentKwh}
                      onChange={(e) => setCurrentKwh(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">kWh / yr</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Check the EnergyGuide label or your fridge spec sheet.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Last cleaned
                  </label>
                  <select
                    value={lastCleaned}
                    onChange={(e) => setLastCleaned(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="6months">6 months ago</option>
                    <option value="1year">1 year ago</option>
                    <option value="2+years">2+ years ago</option>
                    <option value="never">Never</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Cleaning method
                  </label>
                  <select
                    value={cleaningMethod}
                    onChange={(e) => setCleaningMethod(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="diy">DIY ($15 supplies)</option>
                    <option value="professional">Professional service ($100)</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Estimated annual savings
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {`$${result?.annualSavings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">/ yr</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendation ?? "Enter your fridge details."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Payback</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result && Number.isFinite(result.paybackMonths) ? `${result.paybackMonths.toFixed(1)} mo` : "—"}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">5-year savings</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{`$${result?.fiveYearSavings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Replace if needed"
          title="French door fridges to compare"
          description="Modern designs put condenser coils at the front-bottom or rear — much easier to vacuum and keep efficient."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">DOE</em> efficiency loss. EIA rate.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Loss percentages reflect DOE guidance on dirty-coil compressor overhead. Cost = wasted kWh × EIA US average residential rate of $0.13/kWh.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Efficiency-loss bands", d: "Never cleaned: 25%. 2+ years: 20%. 1 year: 10%. 6 months: 5%. Loss compounds compressor runtime." },
                { n: "02", t: "Wasted kWh", d: "Cleaned kWh = current kWh ÷ (1 + loss). Wasted kWh = current − cleaned. Times $0.13 = wasted dollars." },
                { n: "03", t: "Payback math", d: "Cleaning cost (DIY $15 / pro $100) ÷ annual savings × 12 = months to break even." },
                { n: "04", t: "5-year horizon", d: "5 years × annual savings − 2.5 cleaning cycles. Assumes you re-clean every 24 months." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Top-freezer fridges — efficiency leaders"
          description="The most efficient configuration by design. Often beats French door by 100+ kWh/yr."
          collectionType="top-freezer"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
